/**
 * THE LAYOUT RECIPES — the fixes `scripts/visual-qa/layout-audit.mjs` kept
 * finding on screen after screen, made once, here, instead of per screen.
 *
 * Every rule below exists because the audit measured its absence in headless
 * Chromium at 1440x790 and 390x844 (jsdom has no layout and can see none of
 * it). Unlayered on purpose: these are structural guarantees that must beat a
 * Tailwind utility, never a colour or a transform (tokens and press own those).
 *
 * NO BACKTICK anywhere inside the literal — one would end the template string.
 */
export const LAYOUT_CSS = `
/* NAMES WRAP, THEY DO NOT VANISH INTO AN ELLIPSIS.
   Card, crate and wardrobe names were one nowrap line with an ellipsis, so
   "Vaultbreaker" read "Vaultbr..." on every tile. Two balanced lines, hyphens
   where the language allows, and an anywhere-break only as the last resort. */
.rga-name{display:block;min-width:0;max-width:100%;line-height:1.15;text-wrap:balance;
  overflow-wrap:break-word;hyphens:auto;white-space:normal}

/* A BUTTON NEVER ESCAPES ITS ROW. The skin is nowrap (a label that breaks
   mid-press looks broken), so a long label in a narrow cell used to paint
   past the plate. The plate is now capped at its row and the label may take a
   second line instead. */
.rga-btn{max-width:100%;min-width:0}
.rga-btn__label{min-width:0;white-space:normal;text-wrap:balance;overflow-wrap:break-word}

/* A SMALL PLATE STILL TAKES A THUMB. Pills and sm buttons paint 30-38px; an
   invisible ::after grows the finger to 44px tall without moving a pixel of
   paint (the skin uses ::before for its gloss, never ::after). */
.rga-btn--pill::after,.rga-btn--sm::after{content:'';position:absolute;left:0;right:0;
  top:min(0px,calc((100% - 2.75rem) / 2));bottom:min(0px,calc((100% - 2.75rem) / 2))}

/* THE BOTTOM SHEET. Info, notifications and anything a thumb opens from a
   tile: centred panel on a desktop, a sheet that rises from the bottom edge on
   a phone - where the thumb already is. A grab bar says it is a sheet. */
.rga-modal__panel--sheet{width:min(100%,30rem);max-width:min(100%,30rem)}
@media (max-width:40rem){
  .rga-modal:has(> .rga-modal__panel--sheet){place-items:end stretch;padding-inline:0;padding-bottom:0}
  .rga-modal__panel--sheet{width:100%;max-width:100%;max-height:88dvh;
    border-bottom-left-radius:0;border-bottom-right-radius:0;border-bottom-width:0;
    padding-bottom:env(safe-area-inset-bottom);animation:rga-sheet-up 280ms cubic-bezier(.22,1.18,.36,1)}
  .rga-modal__panel--sheet::before{content:'';display:block;width:2.75rem;height:.3rem;border-radius:999px;
    margin:.45rem auto 0;background:var(--color-border)}
}
/* THE POPOVER: on a desktop it hangs from the header's right end, under the
   control that opened it, instead of landing mid-screen; on a phone it is the
   sheet above. */
@media (min-width:40.01rem){
  .rga-modal:has(> .rga-modal__panel--anchor-end){place-items:start end;
    padding-top:calc(var(--rga-header-h,4rem) + .5rem);
    /* Under the header's right end: the header row is centred in the column
       right of the 15rem rail and capped at 75rem, with a 1.5rem gutter. */
    padding-right:max(1rem,calc((100vw - 15rem - 75rem) / 2 + 1.5rem))}
  .rga-modal:has(> .rga-modal__panel--anchor-end) > .rga-modal__scrim{background:rgba(8,14,32,.25)}
  .rga-modal__panel--anchor-end{max-height:calc(100dvh - var(--rga-header-h,4rem) - 1.5rem);
    transform-origin:100% 0}
}
@keyframes rga-sheet-up{0%{translate:0 100%}100%{translate:0 0}}

/* THE INFO DOT: a small round "i" that opens a sheet. The BUTTON is the 44px
   finger (transparent, pulled back by a negative margin so it costs the row
   no height); the 28px disc is its ::before. */
.rga-info{position:relative;isolation:isolate;display:inline-grid;place-items:center;flex:none;
  width:2.75rem;height:2.75rem;margin:-.5rem;padding:0;border:0;background:none;cursor:pointer;
  color:var(--color-text);font:800 .85rem/1 var(--font-heading,inherit)}
.rga-info::before{content:'';position:absolute;inset:.5rem;z-index:-1;border-radius:999px;
  border:var(--rga-rim,2px) solid var(--color-border);background:var(--rga-face,none),var(--color-surface-2);
  box-shadow:var(--rga-lift-1),var(--rga-sheen)}
/* In a chip row the dot keeps its full 44px height, so its finger never
   reaches over the line below (the row is 44px tall, the chips centred). */
.rga-chips > .rga-info,.rga-chips > [data-rga=info-note] > .rga-info{margin-block:0}
.rga-info:hover{color:var(--color-gold)}
.rga-info:hover::before{border-color:var(--color-gold)}

/* ICON + NUMBER CHIPS — the "less text" vocabulary: a clock and 8h, a card
   stack and x3, never a sentence. */
.rga-chips{display:flex;flex-wrap:wrap;align-items:center;gap:.35rem;min-width:0}
.rga-chip{display:inline-flex;align-items:center;gap:.25rem;min-height:1.6rem;padding:.1rem .5rem .1rem .3rem;
  border-radius:999px;background:var(--color-surface-2);border:1px solid var(--color-border);
  box-shadow:var(--rga-depth,none);font-size:.78rem;font-weight:700;font-variant-numeric:tabular-nums;
  color:var(--color-text);white-space:nowrap}
.rga-chip--gold{border-color:color-mix(in srgb,var(--color-gold) 70%,transparent);color:var(--color-gold)}

/* SCREEN TO SCREEN: the new screen rises in, it does not cut. Opacity and a
   short translate only; reduced motion keeps the cut. */
.rga-screen-enter{animation:rga-screen-in 220ms cubic-bezier(.2,.8,.2,1)}
@keyframes rga-screen-in{0%{opacity:0;translate:0 10px}100%{opacity:1;translate:0 0}}

/* THE THUMB BAR: a screen's primary action anchored above the phone tab bar,
   inside the reach of a thumb; inline again from a tablet up. */
/* Children size themselves with utilities: an unlayered child rule here would
   beat every Tailwind flex utility on them (layers, not specificity). The
   phone tab bar is 3.5rem of tab plus its 1px rule; the bar clears it. */
.rga-thumbbar{display:flex;gap:.5rem;align-items:stretch;min-width:0}
@media (max-width:47.99rem){
  .rga-thumbbar--sticky{position:sticky;bottom:calc(3.625rem + env(safe-area-inset-bottom));z-index:5;
    margin-inline:-.25rem;padding:.5rem .25rem;border-radius:var(--rga-radius-panel,14px);
    background:color-mix(in srgb,var(--color-bg,#14213D) 82%,transparent);backdrop-filter:blur(6px)}
}
@media (prefers-reduced-motion:reduce){
  .rga-screen-enter,.rga-modal__panel--sheet{animation:none}
}
`;
